import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Button, InlineStack, Banner } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import { updateDeskCard } from "services/desk.service";

interface RestoreDeskCardConfirmProps {
  companyId: string;
  /** Single-card restore (detail panel / list row). */
  cardNumber?: number;
  /** Title of the single card, so the dialog names what it restores. */
  cardTitle?: string;
  /** Bulk restore from the trash list. */
  cardNumbers?: number[];
  /** When true, restore returns the card to Drafts (isDraft unchanged). */
  isDraft?: boolean;
  onConfirm?: () => void;
}

const RestoreDeskCardConfirm: React.FC<RestoreDeskCardConfirmProps> = ({
  companyId,
  cardNumber,
  cardTitle,
  cardNumbers,
  isDraft,
  onConfirm,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const initialNumbers =
    cardNumbers && cardNumbers.length
      ? cardNumbers
      : cardNumber != null
        ? [cardNumber]
        : [];
  // Remaining ids to restore — shrinks on partial success so retry skips the
  // cards that already came back.
  const [remainingNumbers, setRemainingNumbers] = useState<number[]>(initialNumbers);
  const count = remainingNumbers.length;

  const handleConfirm = async () => {
    if (!count) return;
    const toRestore = remainingNumbers;
    let nextIndex = 0;
    try {
      setIsLoading(true);
      setErrMsg(null);
      for (; nextIndex < toRestore.length; nextIndex++) {
        await updateDeskCard(companyId, toRestore[nextIndex], { deleted: false });
      }
      closeModal();
      toast.success(t("desk_restored_done", { count: toRestore.length }));
      if (onConfirm) onConfirm();
    } catch (e: any) {
      const left = toRestore.slice(nextIndex);
      setRemainingNumbers(left);
      // Refresh the parent so cards that did come back leave the trash list.
      if (left.length < toRestore.length && onConfirm) onConfirm();
      setErrMsg(e?.response?.data?.message || e?.message || t("desk_restore_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  return (
    <div className="space-y-4">
      {errMsg && <Banner tone="critical">{errMsg}</Banner>}
      {/* Name the card so the user can check they picked the right one. */}
      {count === 1 && cardNumber != null && (
        <div className="text-center text-sm text-zinc-500 dark:text-zinc-400">
          #{cardNumber}
          {cardTitle ? ` — ${cardTitle}` : ""}
        </div>
      )}
      <div className="py-2 text-center text-sm font-semibold text-zinc-900 dark:text-zinc-100">
        {count > 1
          ? t("desk_confirm_restore_cards", { count })
          : t(isDraft ? "desk_confirm_restore_draft" : "desk_confirm_restore_card")}
      </div>
      <InlineStack align="space-between">
        <Button onClick={handleConfirm} loading={isLoading} tone="success" variant="primary">
          {t("desk_confirm_restore_card_yes")}
        </Button>
        <Button onClick={closeModal}>{t("desk_confirm_restore_card_no")}</Button>
      </InlineStack>
    </div>
  );
};

export default RestoreDeskCardConfirm;
